import { useState } from 'react'
import { ArrowUpRight, X, Check, Briefcase, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { useMe } from '@/features/auth/hooks'
import { cn } from '@/lib/cn'
import {
  useBulkAssign,
  useBulkClose,
  useBulkEscalate,
} from '../../hooks/useAlertActions'
import { AnalystPicker } from './AnalystPicker'
import { AlertLinker } from '@/features/cases/components/AlertLinker'
import { MAX_BULK_CLOSE_SIZE } from '../../types'
import type { AlertSummary, AnalystSummary } from '../../types'

type ConfirmAction =
  | { kind: 'assign'; user_id: string; assignee_label: string }
  | { kind: 'escalate' }
  | { kind: 'close' }

interface Props {
  selectedIds: readonly string[]
  onClear: () => void
  /**
   * Full alert summaries on the queue page so AlertLinker can
   * derive title / summary / priority / queue defaults from the
   * selected alerts without an extra fetch. Optional — falls back
   * to empty defaults if not provided.
   */
  alerts?: ReadonlyArray<AlertSummary>
}

/**
 * AF-126 — Bulk actions bar.
 *
 * Sticky pill at the bottom-center; inverted color so it reads as decisive.
 * Click → bar morphs into a confirm step in place (no modal context-switch),
 * except "Add to case" which opens the LinkToCaseDialog (case-picker UX).
 */
export function BulkActionsBar({ selectedIds, onClear, alerts }: Props) {
  const selectedSet = new Set(selectedIds)
  const prefillAlerts = (alerts ?? []).filter((a) => selectedSet.has(a.id))
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const { data: me } = useMe()

  const assign = useBulkAssign()
  const close = useBulkClose()
  const escalate = useBulkEscalate()

  if (selectedIds.length === 0) return null

  // Close-specific — see MAX_BULK_CLOSE_SIZE's doc comment. Assign /
  // escalate / add-to-case are NOT bounded by this; they share the
  // handler's separate, larger generic batch cap and must stay usable
  // at selection sizes that would legitimately block a close.
  const overCloseLimit = selectedIds.length > MAX_BULK_CLOSE_SIZE
  const confirmBlocked = confirm?.kind === 'close' && overCloseLimit

  const onConfirm = async (): Promise<void> => {
    if (!confirm) return
    const ids = [...selectedIds]
    // Defense in depth: the "Close as FP" trigger and the Confirm
    // button are both disabled above the cap, but selection can change
    // out from under an already-open confirm step (e.g. the `x`
    // select-all shortcut still works while this panel is showing, or
    // more rows load via infinite scroll). Re-check at submit time
    // rather than trusting the disabled attribute alone — a request
    // that reaches the server over the cap 400s anyway (see
    // `useBulkClose`'s onError), but catching it here avoids a round
    // trip and gives the analyst the same explanatory message either way.
    if (confirm.kind === 'close' && ids.length > MAX_BULK_CLOSE_SIZE) {
      toast.error(`Close is limited to ${MAX_BULK_CLOSE_SIZE} alerts at a time`, {
        description: `${ids.length} alerts selected — deselect ${ids.length - MAX_BULK_CLOSE_SIZE} to continue.`,
      })
      return
    }
    try {
      if (confirm.kind === 'assign') {
        await assign.mutateAsync({ alert_ids: ids, user_id: confirm.user_id })
      } else if (confirm.kind === 'close') {
        await close.mutateAsync({ alert_ids: ids, resolution: 'false_positive' })
      } else if (confirm.kind === 'escalate') {
        await escalate.mutateAsync({
          alert_ids: ids,
          reason: 'Bulk escalation from queue',
        })
      }
    } catch {
      // The mutation's own `onError` already surfaced a toast (with the
      // real reason on bulk-close — see `useBulkClose`). Deliberately do
      // NOT clear the selection or drop the confirm step here: a
      // rejected batch (e.g. bulk-close's whole-batch-rejection 207)
      // means nothing changed server-side, so the analyst should still
      // see exactly which alerts they had selected in order to deselect
      // the blockers and retry, instead of losing their picks.
      return
    }
    setConfirm(null)
    onClear()
  }

  const onPickAnalyst = (a: AnalystSummary | { user_id: 'me' }) => {
    if (a.user_id === 'me') {
      if (!me) return
      setConfirm({ kind: 'assign', user_id: me.id, assignee_label: 'me' })
    } else {
      const sum = a as AnalystSummary
      setConfirm({ kind: 'assign', user_id: sum.user_id, assignee_label: sum.display_name })
    }
  }

  return (
    <>
      <div
        role="region"
        aria-label="Bulk actions"
        className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
      >
        <div className="flex items-center gap-2 rounded-full bg-fg px-3 py-2 text-bg shadow-2xl">
          <span className="flex items-center gap-1 px-1 text-sm">
            <span className="font-mono tabular-nums">{selectedIds.length}</span>
            <span className="text-bg/70">selected</span>
          </span>
          <Separator />

          {confirm ? (
            <>
              <span className="px-2 text-sm">
                {confirmBlocked
                  ? `Close is limited to ${MAX_BULK_CLOSE_SIZE} alerts at a time — deselect ${selectedIds.length - MAX_BULK_CLOSE_SIZE} to continue`
                  : confirmCopy(confirm, selectedIds.length)}
              </span>
              <button
                type="button"
                onClick={() => void onConfirm()}
                disabled={confirmBlocked}
                aria-disabled={confirmBlocked}
                className={cn(
                  'flex items-center gap-1 rounded-full px-3 py-1 text-sm',
                  confirmBlocked
                    ? 'cursor-not-allowed bg-bg/30 text-fg/40'
                    : 'bg-bg text-fg hover:bg-bg/90',
                )}
              >
                <Check size={12} />
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-full px-2 py-1 text-sm text-bg/70 hover:text-bg"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <AnalystPicker
                triggerLabel="Assign…"
                inverted
                onPick={onPickAnalyst}
              />
              <BarButton
                icon={<ArrowUpRight size={12} />}
                label="Escalate"
                onClick={() => setConfirm({ kind: 'escalate' })}
              />
              <BarButton
                icon={<AlertTriangle size={12} />}
                label="Close as FP"
                onClick={() => setConfirm({ kind: 'close' })}
                disabled={overCloseLimit}
                title={
                  overCloseLimit
                    ? `Close is limited to ${MAX_BULK_CLOSE_SIZE} alerts at a time (${selectedIds.length} selected)`
                    : undefined
                }
              />
              {overCloseLimit ? (
                <span
                  role="status"
                  className="flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-400/20 px-2 py-1 text-xs text-amber-200"
                >
                  {MAX_BULK_CLOSE_SIZE} max to close — deselect {selectedIds.length - MAX_BULK_CLOSE_SIZE}
                </span>
              ) : null}
              <BarButton
                icon={<Briefcase size={12} />}
                label="Add to case"
                onClick={() => setLinkOpen(true)}
              />
              <Separator />
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear selection"
                className="rounded-full p-1 text-bg/70 hover:bg-bg/10 hover:text-bg"
              >
                <X size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      <AlertLinker
        open={linkOpen}
        onOpenChange={setLinkOpen}
        alertIds={selectedIds}
        prefillAlerts={prefillAlerts.length > 0 ? prefillAlerts : undefined}
        onLinked={() => onClear()}
      />
    </>
  )
}

function BarButton({
  icon,
  label,
  onClick,
  disabled,
  title,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  /** When true, renders a native `disabled` button — click never fires. */
  disabled?: boolean
  /** Explanatory hover text for the disabled case; visible-by-default
   *  context (e.g. the "N max to close" chip) still carries the same
   *  reason for keyboard/screen-reader users who never hover. */
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      title={title}
      className={cn(
        'flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors',
        disabled ? 'cursor-not-allowed text-bg/40' : 'text-bg hover:bg-bg/10',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function Separator() {
  return <span className="h-4 w-px bg-bg/20" aria-hidden />
}

function confirmCopy(action: ConfirmAction, count: number): string {
  const plural = count === 1 ? 'alert' : 'alerts'
  switch (action.kind) {
    case 'assign':
      return `Assign ${count} ${plural} to ${action.assignee_label}?`
    case 'escalate':
      return `Escalate ${count} ${plural}?`
    case 'close':
      return `Close ${count} ${plural} as false positive?`
  }
}
